'use client';

import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface SidebarSubnavItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  badge?: string | number;
  onSelect?: () => void;
  href?: string;
  /** Optional group header this item sits under (e.g. "Your settings"). */
  group?: string;
}

export interface SidebarSubnavConfig {
  /** Optional href for the back affordance at the top of the subnav. */
  backHref?: string;
  /** Label for the back affordance. Defaults to "Back to app". */
  backLabel?: string;
  /** Section title shown above the items (e.g. "Settings"). */
  title: string;
  /** Currently selected item id. */
  activeId?: string;
  /** Subnav items rendered in order. */
  items: SidebarSubnavItem[];
}

interface SidebarSubnavContextValue {
  subnav: SidebarSubnavConfig | null;
  setSubnav: (subnav: SidebarSubnavConfig | null) => void;
}

const SidebarSubnavContext = createContext<SidebarSubnavContextValue | undefined>(undefined);

export function SidebarSubnavProvider({ children }: { children: ReactNode }) {
  const [subnav, setSubnav] = useState<SidebarSubnavConfig | null>(null);
  const value = useMemo(() => ({ subnav, setSubnav }), [subnav]);
  return <SidebarSubnavContext.Provider value={value}>{children}</SidebarSubnavContext.Provider>;
}

export function useSidebarSubnav() {
  const ctx = useContext(SidebarSubnavContext);
  if (!ctx) {
    throw new Error('useSidebarSubnav must be used within SidebarSubnavProvider');
  }
  return ctx;
}
